import { useMemo, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FaceRoundedIcon from '@mui/icons-material/FaceRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import { alpha, useTheme } from '@mui/material/styles';
import type {
  DedupPolicyTier,
  IdentityLookupRow,
  IdentityLookupUploadedImage,
} from '../../api/identityVerificationDashboardApi';
import { resolveIdentitySessionSummary, type IdentitySummaryTone } from '../../lib/identitySessionSummary';
import {
  resolveCustomerMatchOutcome,
  resolveReturningCustomerOutcome,
  type IdentityEvidenceOutcome,
} from '../../lib/identityEvidenceQuestions';
import {
  summarizeDedupWhatMatched,
  type IdentityAttemptRole,
} from '../../lib/identityAttemptPresentation';
import { ImagePreviewDialog, UploadedImagePreview } from '../../pages/IdentityVerificationLookup';
import { BiometricMatchComparisonPanel } from './BiometricMatchComparisonPanel';
import {
  ApplicationSectionHeader,
  ApplicationSectionIcon,
} from './applicationSectionChrome';

const UPLOAD_TYPE_ORDER: Record<string, number> = {
  selfie: 0,
  id_front: 1,
  id_back: 2,
};

const kvInsetSx = {
  px: 1,
  borderRadius: 1.5,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: (t: { palette: { mode: string; common: { white: string } } }) =>
    t.palette.mode === 'light' ? '#F7F8FA' : alpha(t.palette.common.white, 0.04),
} as const;

function uploadTypeLabel(imageType: string): string {
  switch (imageType) {
    case 'selfie':
      return 'Selfie';
    case 'id_front':
      return 'ID front';
    case 'id_back':
      return 'ID back';
    default:
      return imageType;
  }
}

function Kv({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(110px, 140px) 1fr' },
        columnGap: 1.5,
        rowGap: 0.25,
        py: 0.85,
        alignItems: 'baseline',
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
        {value}
      </Typography>
    </Box>
  );
}

function EvidenceCard({
  title,
  subtitle,
  icon,
  children,
  action,
}: {
  title: string;
  subtitle?: string;
  icon: ReactNode;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        height: '100%',
        minWidth: 0,
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1.25 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <ApplicationSectionHeader title={title} subtitle={subtitle} icon={icon} mb={0} />
        </Box>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function toneChipColor(
  tone: IdentitySummaryTone,
): 'success' | 'warning' | 'error' | 'default' | 'info' {
  switch (tone) {
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    case 'info':
      return 'info';
    default:
      return 'default';
  }
}

function toneIconColor(
  tone: IdentitySummaryTone,
): 'primary' | 'success' | 'warning' | 'error' | 'info' {
  const chip = toneChipColor(tone);
  return chip === 'default' ? 'primary' : chip;
}

function EvidenceOutcomeSection({
  outcome,
  icon,
  children,
}: {
  outcome: IdentityEvidenceOutcome;
  icon: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Box sx={{ ...kvInsetSx, p: 1.5 }}>
      <ApplicationSectionHeader
        title={outcome.title}
        subtitle={outcome.body}
        icon={icon}
        iconColor={toneIconColor(outcome.tone)}
        endAdornment={
          <Chip
            size="small"
            color={toneChipColor(outcome.tone)}
            variant="outlined"
            label={outcome.answer}
            sx={{ fontWeight: 700, height: 22 }}
          />
        }
        mb={children ? 1.25 : 0}
      />
      {children}
    </Box>
  );
}

function CustomerProfileGrid({
  title,
  customer,
  whatMatched,
  matchChipColor = 'default',
}: {
  title: string;
  customer: {
    ccid: string;
    internal_id: string;
    status: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone_number: string | null;
    date_of_birth: string | null;
    residential_address: string | null;
    created_at: string;
    updated_at: string;
  };
  whatMatched?: string[];
  matchChipColor?: 'default' | 'warning';
}) {
  const name = [customer.first_name, customer.last_name].filter(Boolean).join(' ').trim();
  return (
    <Box sx={kvInsetSx}>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={700}
        sx={{ display: 'block', pt: 0.85, px: 0.25 }}
      >
        {title}
      </Typography>
      <Kv label="Name" value={name || '—'} />
      <Divider />
      <Kv label="Customer id" value={customer.ccid} />
      {whatMatched && whatMatched.length > 0 ? (
        <>
          <Divider />
          <Box sx={{ py: 0.85 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 600, fontSize: '0.75rem', mb: 0.75 }}
            >
              What matched
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {whatMatched.map((line) => (
                <Chip
                  key={line}
                  size="small"
                  label={line}
                  color={matchChipColor}
                  variant="outlined"
                  sx={{ height: 22, fontWeight: 600 }}
                />
              ))}
            </Stack>
          </Box>
        </>
      ) : null}
    </Box>
  );
}

export function ApplicationIdentityAttemptCard({
  row,
  policyTiers,
  isLatest = true,
  role = 'identity_check',
  roleLabel,
  roleHint,
  hidePrimaryOutcome = false,
}: {
  row: IdentityLookupRow;
  policyTiers: DedupPolicyTier[];
  isLatest?: boolean;
  role?: IdentityAttemptRole;
  roleLabel?: string;
  roleHint?: string;
  /** When Match neighborhood / After the match already cover Customer match or Returning check. */
  hidePrimaryOutcome?: boolean;
}) {
  const theme = useTheme();
  const dedup = row.latestDedup;
  const isReturningFlow = role === 'after_signin';
  const isExistingFound = role === 'existing_found';
  const summary = useMemo(() => resolveIdentitySessionSummary(row), [row]);
  const primaryOutcome = useMemo(
    () => (isReturningFlow ? resolveReturningCustomerOutcome(row) : resolveCustomerMatchOutcome(row)),
    [row, isReturningFlow],
  );
  const [photosOpen, setPhotosOpen] = useState(false);
  const [zoom, setZoom] = useState<{
    image: IdentityLookupUploadedImage;
    url: string;
    contentType: string;
  } | null>(null);

  const tiersForDisplay = useMemo(() => {
    const byTier = new Map((dedup?.tier_evaluation ?? []).map((t) => [t.tier, t]));
    const fromPolicy = policyTiers.map(
      (p) =>
        byTier.get(p.tier) ?? {
          tier: p.tier,
          label: p.label,
          hit: false,
          evaluated: false,
          details: {},
        },
    );
    if (fromPolicy.length > 0) return fromPolicy;
    return dedup?.tier_evaluation ?? [];
  }, [dedup?.tier_evaluation, policyTiers]);

  const whatMatched = useMemo(
    () => summarizeDedupWhatMatched(tiersForDisplay, dedup?.message),
    [tiersForDisplay, dedup?.message],
  );

  const hitCustomer = dedup?.matched_hit_customer ?? null;
  const biometricComparison = row.biometricMatchComparison ?? null;

  const customerUploads = useMemo(() => {
    return [...row.uploadedImages].sort((a, b) => {
      const ao = UPLOAD_TYPE_ORDER[a.image_type] ?? 50;
      const bo = UPLOAD_TYPE_ORDER[b.image_type] ?? 50;
      if (ao !== bo) return ao - bo;
      return a.sequence_index - b.sequence_index;
    });
  }, [row.uploadedImages]);

  const documentUploads = useMemo(
    () => customerUploads.filter((u) => u.image_type !== 'selfie'),
    [customerUploads],
  );

  const summaryChipColor =
    summary.tone === 'success'
      ? 'success'
      : summary.tone === 'warning'
        ? 'warning'
        : summary.tone === 'error'
          ? 'error'
          : 'default';

  const showOutcomeBody =
    summary.tone !== 'success' &&
    // Existing-found roleHint already carries the story; What matched chips list the tiers.
    !(isExistingFound && Boolean(roleHint));

  const returningComparisons = useMemo(() => {
    if (!isReturningFlow) return [] as string[];
    const lines: string[] = [];
    const bio = biometricComparison;
    const route = String(row.verification_route ?? '').toUpperCase();
    const status = String(row.session_status ?? '').toUpperCase();
    const completedOk =
      status === 'SUCCESS' || status === 'COMPLETED' || status === 'IDV_COMPLETED';

    if (bio?.kind === 'RETURNING_SELFIE_1TO1') {
      lines.push(
        bio.passed === true || (bio.passed !== false && completedOk)
          ? 'Login selfie matched on-file selfie'
          : bio.passed === false
            ? 'Login selfie did not match on-file selfie'
            : 'Login selfie compared to on-file selfie',
      );
      if (bio.distance != null && bio.threshold != null) {
        lines.push(`Distance ${bio.distance} (threshold ${bio.threshold})`);
      }
    } else if (bio?.kind === 'DEDUP_BIOMETRIC_HIT') {
      lines.push(
        bio.passed === true
          ? 'Biometric comparison matched the account on file'
          : bio.passed === false
            ? 'Biometric comparison did not match'
            : 'Biometric comparison ran against the account on file',
      );
    } else if (route === 'RETURNING_SELFIE_1TO1' && completedOk) {
      lines.push('Returning biometric selfie check passed');
    } else {
      lines.push('Signed-in account continuity confirmed');
      if (dedup?.isDuplicate === false) {
        lines.push('No other customer record matched this submission');
      }
    }
    return lines;
  }, [isReturningFlow, biometricComparison, dedup?.isDuplicate, row.verification_route, row.session_status]);

  // No-match (new customer): outcome chip + body only — do not show Name/CCID
  // "Customer on file" (that is this attempt’s new profile, not a match).
  const showPrimaryBody = isReturningFlow
    ? Boolean(row.customer || row.ccid || returningComparisons.length > 0)
    : Boolean(dedup?.isDuplicate && (hitCustomer || whatMatched.length > 0));

  const primaryIcon = isReturningFlow ? (
    <HowToRegRoundedIcon fontSize="small" />
  ) : (
    <PersonSearchRoundedIcon fontSize="small" />
  );

  // After sign-in: What’s happening already tells the OTP story — skip role chip/hint and Document/Checked KV.
  // Clean new-customer pass (Passed + No match found): header + Photos + Passed only —
  // Document/Checked and Customer match add no operator value (Match neighborhood empty + Passed already say it).
  const isCleanNoMatchPass =
    !isReturningFlow &&
    !isExistingFound &&
    summary.tone === 'success' &&
    dedup?.isDuplicate === false;
  const showRoleChrome = !isReturningFlow;
  const showSummaryFields = !isReturningFlow && !isCleanNoMatchPass;
  const showCustomerMatchSection = !hidePrimaryOutcome && !isCleanNoMatchPass;
  const statusChipLabel =
    isReturningFlow && /^Passed/i.test(summary.title) ? 'Passed' : summary.title;
  const compactCard = (hidePrimaryOutcome && isReturningFlow) || isCleanNoMatchPass;

  const accentMain = isExistingFound
    ? theme.palette.warning.main
    : theme.palette.primary.main;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: compactCard ? 1.35 : 1.75,
        borderRadius: 2,
        borderColor: isExistingFound
          ? alpha(accentMain, 0.5)
          : isLatest
            ? alpha(accentMain, 0.28)
            : 'divider',
        bgcolor: isExistingFound
          ? alpha(accentMain, theme.palette.mode === 'light' ? 0.045 : 0.1)
          : 'background.paper',
        boxShadow: isExistingFound
          ? `inset 3px 0 0 ${alpha(accentMain, 0.85)}`
          : undefined,
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: showCustomerMatchSection || showSummaryFields || showOutcomeBody ? 1.25 : 0 }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <ApplicationSectionIcon color={isExistingFound ? 'warning' : 'primary'}>
            <FingerprintRoundedIcon fontSize="small" />
          </ApplicationSectionIcon>
          <Stack spacing={0.35} sx={{ pt: 0.2, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
              {showRoleChrome && roleLabel ? (
                <Chip
                  size="small"
                  color={isExistingFound ? 'warning' : 'primary'}
                  variant={isExistingFound ? 'filled' : 'outlined'}
                  label={roleLabel}
                  sx={{ height: 22, fontWeight: 700 }}
                />
              ) : null}
              {isLatest ? (
                <Chip
                  size="small"
                  color={isExistingFound ? 'warning' : 'primary'}
                  variant="outlined"
                  label="Latest"
                  sx={{ height: 22 }}
                />
              ) : null}
            </Stack>
            {showRoleChrome && roleHint ? (
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                {roleHint}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PhotoLibraryRoundedIcon fontSize="small" />}
            onClick={() => setPhotosOpen(true)}
            sx={{ height: 28, textTransform: 'none', fontWeight: 700, flexShrink: 0 }}
          >
            Photos
            {customerUploads.length > 0 ? ` (${customerUploads.length})` : ''}
          </Button>
          <Chip
            size="small"
            color={summaryChipColor}
            variant="outlined"
            label={statusChipLabel}
            sx={{ fontWeight: 700, flexShrink: 0, height: 22 }}
          />
        </Stack>
      </Stack>

      {showSummaryFields && summary.fields.length > 0 ? (
        <Box sx={{ ...kvInsetSx, mb: 1.5 }}>
          {summary.fields.map((field, i) => (
            <Box key={`${field.label}-${field.value}`}>
              {i > 0 ? <Divider /> : null}
              <Kv label={field.label} value={field.value} />
            </Box>
          ))}
        </Box>
      ) : null}

      {showOutcomeBody ? (
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45, mb: 1.5 }}>
          {summary.body}
        </Typography>
      ) : null}

      {showCustomerMatchSection ? (
      <Stack spacing={1.5}>
          <EvidenceOutcomeSection outcome={primaryOutcome} icon={primaryIcon}>
            {showPrimaryBody ? (
              <Stack spacing={1.25}>
                {isReturningFlow ? (
                  <>
                    {returningComparisons.length > 0 ? (
                      <Box sx={kvInsetSx}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={700}
                          sx={{ display: 'block', pt: 0.85, px: 0.25, pb: 0.5 }}
                        >
                          Comparisons run
                        </Typography>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ px: 0.25, pb: 1 }}>
                          {returningComparisons.map((line) => (
                            <Chip
                              key={line}
                              size="small"
                              label={line}
                              variant="outlined"
                              sx={{ height: 22, fontWeight: 600 }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    ) : null}
                    {row.customer ? (
                      <CustomerProfileGrid title="Account on file" customer={row.customer} />
                    ) : null}
                  </>
                ) : (
                  <>
                    {dedup?.isDuplicate && hitCustomer ? (
                      <CustomerProfileGrid
                        title="Matched existing customer"
                        customer={hitCustomer}
                        whatMatched={whatMatched}
                        matchChipColor={isExistingFound ? 'warning' : 'default'}
                      />
                    ) : null}
                    {dedup?.isDuplicate && !hitCustomer && whatMatched.length > 0 ? (
                      <Box sx={kvInsetSx}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={700}
                          sx={{ display: 'block', pt: 0.85, px: 0.25, pb: 0.5 }}
                        >
                          What matched
                        </Typography>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ px: 0.25, pb: 1 }}>
                          {whatMatched.map((line) => (
                            <Chip
                              key={line}
                              size="small"
                              label={line}
                              color={isExistingFound ? 'warning' : 'default'}
                              variant="outlined"
                              sx={{ height: 22, fontWeight: 600 }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    ) : null}
                  </>
                )}
              </Stack>
            ) : null}
          </EvidenceOutcomeSection>
      </Stack>
      ) : null}

      <Dialog
        open={photosOpen}
        onClose={() => setPhotosOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        aria-labelledby="identity-attempt-photos-title"
      >
        <DialogTitle id="identity-attempt-photos-title" sx={{ pr: 6 }}>
          Photos
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontWeight: 400 }}>
            {biometricComparison
              ? 'Selfie match and ID photos from this attempt.'
              : 'Selfie and ID photos from this attempt.'}
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={() => setPhotosOpen(false)}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1.5 }}>
          {biometricComparison ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.1fr) minmax(0, 0.9fr)' },
                gap: 1.5,
                alignItems: 'stretch',
              }}
            >
              <EvidenceCard
                title="Selfie match"
                subtitle="This attempt compared to the on-file selfie"
                icon={<FaceRoundedIcon fontSize="small" />}
              >
                <BiometricMatchComparisonPanel comparison={biometricComparison} onZoom={setZoom} />
              </EvidenceCard>

              <EvidenceCard
                title="Also submitted"
                subtitle="ID photos from this attempt"
                icon={<BadgeRoundedIcon fontSize="small" />}
              >
                {documentUploads.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No ID photos recorded for this attempt.
                  </Typography>
                ) : (
                  <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap">
                    {documentUploads.map((u) => (
                      <Stack key={u.id} spacing={0.4} alignItems="center" sx={{ width: 104 }}>
                        <UploadedImagePreview
                          image={u}
                          size={100}
                          onClick={(url, contentType) => setZoom({ image: u, url, contentType })}
                        />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          {uploadTypeLabel(u.image_type)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </EvidenceCard>
            </Box>
          ) : customerUploads.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No uploaded images recorded for this attempt yet.
            </Typography>
          ) : (
            <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap">
              {customerUploads.map((u) => (
                <Stack key={u.id} spacing={0.4} alignItems="center" sx={{ width: 104 }}>
                  <UploadedImagePreview
                    image={u}
                    size={100}
                    onClick={(url, contentType) => setZoom({ image: u, url, contentType })}
                  />
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {uploadTypeLabel(u.image_type)}
                    {u.sequence_index > 0 ? ` #${u.sequence_index}` : ''}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <ImagePreviewDialog zoom={zoom} onClose={() => setZoom(null)} />
    </Paper>
  );
}

/** @deprecated Use ApplicationIdentityAttemptCard */
export const ApplicationIdentitySessionCard = ApplicationIdentityAttemptCard;
